import { SelectOption } from '@globaleris/ui.library'
import { updatedDiff } from 'deep-object-diff'
import moment, { Moment } from 'moment'
import {
  EnumCommodity,
  EnumEstadoCliente,
  EnumEstadoProductoCapacidad,
  EnumServicio,
  EnumTipoContrato,
  EnumTipoPeaje,
  EnumTipoProductoCapacidad,
  EnumTipoSujeto,
  EnumZonaBalance
} from '../../api'
import {
  AnnexedContractModel,
  AnnexedContractModelPatchRequest,
  AnnexedContractModelPostRequest,
  ContractOrdersModel,
  ContractOrdersRequestModel,
  ContractQuantityDetailRequest,
  ContractQuantityRequest,
  ContractSummaryModel,
  NewContractQuantity,
  NewContractQuantityDetail
} from '../../api/Argentia/endpoints/contracts/model/contracts'
import { EnumPeriodo } from '../../api/Argentia/enums/periodo'
import {
  ActiveContractModelResponse,
  ActivityTypeModel,
  CUPSModel,
  CapacityModel,
  ContractATRModel,
  ContractDetailATRModel,
  ContractModel,
  DistributorModel,
  NewCUPSModel,
  NewContractATRModel,
  NewContractDetailATRModel,
  NewProductModel,
  PaymentMethodTypeModel,
  PowerModel,
  ProductModel,
  TollModel
} from '../../api/Argentia/model/contracts'
import { Account, AccountTableFormat } from '../../api/Argentia/model/subjects'
import { ContractProductAPIModel } from '../../api/Pioneer/model/Contracts'
import { EnumTipoMetodoPago } from '../../api/oData/enums/enumTipoMetodoPago'
import {
  enumEnumToValueNumber,
  enumKeyToValue,
  enumTranslateOption,
  enumValueToKey,
  isEmpty,
  toLocaleDate
} from '../../helpers'
import { associateActionIcon } from '../../helpers/dataTableActionIconHelper'
import { formatDateOnlyDate } from '../../helpers/formatDateOnly'
import { ActionIcons } from '../../model'
import { getActiveBalanceZone, getActiveCompany } from '../../redux'
import { FiscalAddressModel, SubjectModel } from '../clients/clients.model'
import { Limit, LimitDetail } from '../flexibility/flexibility.Model'
import { ChangeOwnerForm } from './changeOwner/contracts.changeOwner'
import {
  CapacityFormModel,
  ContractATRAddVersionFormModel,
  ContractATRFormModel,
  ContractDataFormModel,
  ContractForm,
  ContractFormRaw,
  ContractGeneralDataFormModel,
  ContractProduct,
  CupsFormModel,
  DBContractSuppliesProps,
  DeleteContractProductModel,
  NewAddressModel,
  NewContractModel,
  NewContractProduct,
  NewPaymentMethodModel,
  PatchAccountModel,
  PatchAddressModel,
  PatchCUPSModel,
  PatchCapacity,
  PatchContractATR,
  PatchContractATRDetail,
  PatchContractBatchRequest,
  PatchContractModel,
  PatchContractProductModel,
  PatchContractSuppliesBatchRequest,
  PatchPaymentMethodModel,
  PatchPower,
  Period,
  SuppliesForm
} from './contracts.model'
import { ATRVersionTableModel } from './details/components/versionsTable'
import {
  AnnexedParamsModel,
  AnnexedParamsValuesModel
} from '../../api/Argentia/endpoints/annexedParams/model/annexedParams'
import { AnnexedContractForm } from './edit/components/annexedContracts'
import { EnumTipoValor } from '../../api/oData/enums/enumTipoValor'
import { EnumHold } from '../../api/oData/enums/enumHold'
import { EnumPresionTension } from '../../api/oData/enums/enumPresionTension'

export const listTollItemMapper = (items: TollModel[]): SelectOption[] => {
  return items && items.map(tollItemMapper)
}

export const tollItemMapper = (item: TollModel): SelectOption => {
  return (
    item && {
      value: item.id,
      label: item.description
    }
  )
}

export const listDistributorItemMapper = (items: DistributorModel[]): SelectOption[] => {
  return items && items.map(distributorItemMapper)
}

export const distributorItemMapper = (item: DistributorModel): SelectOption => {
  return (
    item && {
      value: item.id!,
      label: item.description
    }
  )
}

export const listCnaeItemMapper = (items: ActivityTypeModel[]): SelectOption[] => {
  return items && items.map(cnaeItemMapper)
}

export const cnaeItemMapper = (item: ActivityTypeModel): SelectOption => {
  return (
    item && {
      value: item.id!,
      label: `${item.code ?? ''} - ${item.description}`
    }
  )
}

export const paymentMethodTypesToOptionsMapper = (
  methods: PaymentMethodTypeModel[]
): SelectOption[] => {
  return methods && methods.map(paymentMethodTypeMapper)
}

export const paymentMethodTypeMapper = (method: PaymentMethodTypeModel): SelectOption => {
  return (
    method && {
      value: method.Id,
      label: method.description
    }
  )
}

export const newContractMapper = (
  contractDataForm: ContractDataFormModel,
  address: NewAddressModel,
  commodity: EnumCommodity,
  switchingRole: boolean,
  subjectType?: EnumTipoSujeto
): NewContractModel => {
  const activeCompany = getActiveCompany()
  const activeBalanceZone = getActiveBalanceZone()?.id

  const parseStartDate = moment(contractDataForm.startDate!).format('YYYY-MM-DD')
  const parseEndDate = contractDataForm.endDate
    ? moment(contractDataForm.endDate).format('YYYY-MM-DD')
    : undefined
  const parseSignDate = moment(contractDataForm.signDate!).format('YYYY-MM-DD')
  const type = enumEnumToValueNumber(EnumTipoSujeto, subjectType)

  return (
    contractDataForm && {
      contractCode: contractDataForm.contractCode!,
      status: switchingRole ? EnumEstadoCliente.Borrador : EnumEstadoCliente.Activo,
      type: contractDataForm.contractType,
      // type:
      //   type && type === EnumTipoSujeto.Mayorista
      //     ? EnumTipoContrato.Trade
      //     : type && type === EnumTipoSujeto.Comercial
      //       ? EnumTipoContrato.ClienteComercial
      //       : commodity === EnumCommodity.GasNatural
      //         ? EnumTipoContrato.ClienteFinal
      //         : EnumTipoContrato.Electricidad,
      startDate: parseStartDate,
      endDate: parseEndDate,
      signatureDate: parseSignDate,
      balanceZone: enumValueToKey(EnumZonaBalance, activeBalanceZone!),
      paymentMethod: {
        type: contractDataForm.paymentMethod!,
        iban: contractDataForm.iban!,
        address
      },
      billingCycleID: contractDataForm.billingCycle!,
      subjectID: contractDataForm.subjectID,
      cupsList: newCupsMapper(contractDataForm, address, commodity),
      productsList: newProductsMapper(contractDataForm),
      companyID: activeCompany?.id!,
      commodity: commodity,
      description: contractDataForm.description,
      hold: contractDataForm.hold,
      bookID: contractDataForm.book,
      contractOrders: contractDataForm.orderNm ? [mapContractDataFormOrders(contractDataForm)] : []
    }
  )
}

export const mapContractDataFormOrders = (
  formData: ContractDataFormModel
): ContractOrdersRequestModel => {
  return {
    orderNm: formData.orderNm,
    orderVersion: 1,
    startDate: moment(formData.startDate).format('YYYY-MM-DD'),
    endDate: moment(formData.endDate).format('YYYY-MM-DD')
  }
}

export const newPaymentMethodMapper = (
  contractDataForm: ContractDataFormModel,
  client: SubjectModel
): NewPaymentMethodModel => {
  return (
    contractDataForm && {
      type: contractDataForm.paymentMethod!,
      iban: contractDataForm.iban!,
      address: newAddressMapper(client)
    }
  )
}

export const newCupsMapper = (
  contractDataForm: ContractDataFormModel,
  address: NewAddressModel,
  commodity: EnumCommodity
): NewCUPSModel[] => {
  return contractDataForm && contractDataForm.cups
    ? [
        {
          code: contractDataForm.cups!,
          ...(!contractDataForm.exists && { fiscalAddress: address }),
          capacitiesList:
            commodity === EnumCommodity.GasNatural ? capacityMapper(contractDataForm) : undefined,
          contractATRList:
            commodity === EnumCommodity.Electricidad
              ? contractATRMapper(contractDataForm)
              : undefined,
          exists: contractDataForm.exists,
          telemetered: contractDataForm.telemetered!,
          distributor: contractDataForm.distributorModel!,
          cnae: contractDataForm.cnaeModel?.code,
          pressureGroupId: contractDataForm.pressureGroup!
        }
      ]
    : []
}

export const newProductsMapper = (contractDataForm: ContractDataFormModel): NewProductModel[] => {
  return contractDataForm.productsList && contractDataForm.productsList.length > 0
    ? contractDataForm.productsList.map(element => {
        return {
          id: element.id,
          code: element.code!,
          cupsList:
            contractDataForm && contractDataForm.cups ? [{ code: contractDataForm.cups! }] : []
        }
      })
    : []
}

export const fiscalAddressMapper = (clientData: SubjectModel): FiscalAddressModel => {
  return (
    clientData && {
      postalAddress: clientData.fiscalAddress.postalAddress,
      postalCode: clientData.fiscalAddress.postalCode,
      municipality: clientData.fiscalAddress.municipality,
      province: clientData.fiscalAddress.province,
      country: clientData.fiscalAddress.country
    }
  )
}

export const newAddressMapper = (clientData: SubjectModel): NewAddressModel => {
  return (
    clientData && {
      postalAddress: clientData.fiscalAddress.postalAddress,
      postalCode: clientData.fiscalAddress.postalCode,
      municipality: clientData.fiscalAddress.municipality,
      province: clientData.fiscalAddress.province,
      country: clientData.fiscalAddress.country,
      municipalityId: clientData.fiscalAddress.municipalityId!,
      provinceId: clientData.fiscalAddress.provinceId!,
      countryId: clientData.fiscalAddress.countryId!
    }
  )
}

export const capacityMapper = (contractDataForm: ContractDataFormModel): CapacityModel[] => {
  return (
    contractDataForm && [
      {
        code: contractDataForm.contractCode!,
        status: EnumEstadoProductoCapacidad.Formalizada,
        type: contractDataForm.contractCapacityType ?? EnumTipoProductoCapacidad.Anual,
        contractType: contractDataForm.service!,
        toll: contractDataForm.toll!,
        qaEstimada: contractDataForm.qaEstimated!,
        qdContratada: contractDataForm.qdHired
      }
    ]
  )
}

export const contractATRMapper = (
  contractDataForm: ContractDataFormModel
): NewContractATRModel[] => {
  const activeCompany = getActiveCompany()

  return (
    contractDataForm && [
      {
        code: contractDataForm.aTRCode!,
        type: contractDataForm.aTRType!,
        state: EnumEstadoProductoCapacidad.Activo,
        signDate: contractDataForm.signDate,
        versions: contractATRDetailMapper(contractDataForm),
        companyID: activeCompany?.id!
      }
    ]
  )
}

export const contractATRDetailMapper = (
  contractDataForm: ContractDataFormModel
): NewContractDetailATRModel[] => {
  return (
    contractDataForm && [
      {
        startDate: contractDataForm.startDate!,
        endDate: contractDataForm.endDate,
        tollType: contractDataForm.toll,
        version: 1,
        powers: powersMapper(contractDataForm)
      }
    ]
  )
}

export const mapDbContractDetails = (contract: ContractModel): DBContractSuppliesProps => {
  return {
    cups:
      contract.cupsList && contract.cupsList.length > 0 && contract.cupsList[0]
        ? cupsModelToFormModelMapper(contract.cupsList[0])
        : null,
    contractATR: contract.contractATR ? contractModelToATRMapper(contract.contractATR) : null,
    capacities:
      contract.capacities && contract.capacities.length > 0
        ? capacityModelListToFormModelMapper(contract.capacities)
        : null
  }
}

export const powersMapper = (contractDataForm: ContractDataFormModel): PowerModel[] => {
  var periods: PowerModel[] = []
  if (contractDataForm && contractDataForm.periodsNum && contractDataForm.periodsNum > 0) {
    for (var i = 1; i <= contractDataForm.periodsNum; i++) {
      var periodValue = 0
      var period = EnumPeriodo.Periodo1

      switch (i) {
        case 1:
          periodValue = contractDataForm.period1!
          period = EnumPeriodo.Periodo1
          break
        case 2:
          periodValue = contractDataForm.period2!
          period = EnumPeriodo.Periodo2
          break
        case 3:
          periodValue = contractDataForm.period3!
          period = EnumPeriodo.Periodo3
          break
        case 4:
          periodValue = contractDataForm.period4!
          period = EnumPeriodo.Periodo4
          break
        case 5:
          periodValue = contractDataForm.period5!
          period = EnumPeriodo.Periodo5
          break
        case 6:
          periodValue = contractDataForm.period6!
          period = EnumPeriodo.Periodo6
          break
      }

      periods.push({
        value: periodValue,
        period: period
      })
    }
  }

  return periods
}

export const updateContractGeneralDataMapper = (
  contractDataForm: ContractGeneralDataFormModel,
  contractID: number
): PatchContractModel => {
  const parseStartDate = moment(contractDataForm.startDate!).format('YYYY-MM-DD')
  const parseEndDate = contractDataForm.endDate
    ? moment(contractDataForm.endDate).format('YYYY-MM-DD')
    : undefined
  const parseSignDate = moment(contractDataForm.signDate!).format('YYYY-MM-DD')

  return (
    contractDataForm && {
      id: contractID,
      contractCode: contractDataForm.contractCode!,
      startDate: parseStartDate,
      endDate: parseEndDate,
      signatureDate: parseSignDate,
      status: contractDataForm.state
    }
  )
}

export const updateContractBatchMapper = (
  contractID: number,
  contractDataForm: ContractForm,
  contract: ContractForm
): PatchContractBatchRequest => {
  const newContract = getDiffContract(contract, contractDataForm)
  const newAccount = getDiffAccount(contract, contractDataForm)
  const newAddress = getDiffAddress(contract, contractDataForm)
  const newPayment = getDiffPayment(contract, contractDataForm)
  const newProducts = getDiffProductsList(contractDataForm.products, {
    cupsId: contractDataForm.cupsId,
    contractId: contractDataForm.id
  })
  const delProducts = getDeletedProducts(contractDataForm.products, contract.productsList)
  return {
    contract: newContract
      ? { ...newContract, bookID: newContract.bookID ?? 0, id: contractID, hold: newContract.hold }
      : undefined,
    account: newAccount ? { ...newAccount, id: contract.accountId } : undefined,
    paymentMethod: newPayment ? { ...newPayment, id: contract.paymentMethodId } : undefined,
    address: newAddress ? { ...newAddress, id: contract.fiscalAddressId } : undefined,
    products: newProducts,
    deletedProducts: delProducts
  }
}

export const updateContractATRSuppliesBatchMapper = (
  formData: SuppliesForm,
  dbData: SuppliesForm
): PatchContractSuppliesBatchRequest => {
  const newCups = getDiffCups(formData.cups, dbData.cups)
  const newAddress = getDiffCupsAddress(formData.cups, dbData.cups)

  let newContractATR: PatchContractATR | null = null

  if (formData.contractATR) {
    newContractATR = dbData.contractATR
      ? getDiffContractATR(formData.contractATR, dbData.contractATR)
      : null

    // ContractATR id NOT EXIST: POST ContractATR
    if (dbData.contractATR && dbData.contractATR.id === undefined) {
      const {
        newContractATR: postContractATR,
        newContractATRDetail: postDetails,
        newPowers: postPowers
      } = getNewContractATR(formData.contractATR)
      newContractATR = {
        ...postContractATR,
        cupsId: dbData.cups.id,
        state: EnumEstadoProductoCapacidad.Activo
      }
    }
  }

  return {
    cups: newCups ? { ...newCups, id: formData.cups.id } : undefined,
    address: newAddress ? newAddress : undefined,
    contractATR: newContractATR ? newContractATR : undefined
  }
}

export const updateContractCapacitiesSuppliesBatchMapper = (
  formData: SuppliesForm,
  dbData: SuppliesForm
): PatchContractSuppliesBatchRequest => {
  const newCups = getDiffCups(formData.cups, dbData.cups)
  const newAddress = getDiffCupsAddress(formData.cups, dbData.cups)

  let capacities: PatchCapacity[] | null = null

  if (formData.capacities && formData.capacities.length > 0) {
    // Modified Capacities
    if (dbData.capacities && dbData.capacities.length > 0) {
      capacities = getDiffCapacities(formData.capacities, dbData.capacities)
    }

    let capacitiesTemp: PatchCapacity[] = []
    // New Capacities and Delete Capacities
    formData.capacities.forEach(cap => {
      if (!cap.id && !cap.delete) {
        const newCapacity = newCapacitiesFormModelToPatchModelMapper(cap)
        capacitiesTemp.push(newCapacity)
      }
      if (cap.id && cap.delete) {
        capacitiesTemp.push({ id: cap.id, delete: cap.delete })
      }
    })

    if (capacities !== null) {
      capacities = [...capacities, ...capacitiesTemp]
    } else {
      capacities = [...capacitiesTemp]
    }
  }

  return {
    cups: newCups ? { ...newCups, id: formData.cups.id } : undefined,
    address: newAddress ? newAddress : undefined,
    capacities: capacities && capacities.length > 0 ? capacities : undefined
  }
}

const getDiffContract = (
  contract: ContractForm,
  formData: ContractForm
): Omit<PatchContractModel, 'id'> | null => {
  const oldContract = contractModelToPatchContractModelMapper(contract)
  const newContract = contractModelToPatchContractModelMapper(formData)

  return !isEmpty(updatedDiff(oldContract, newContract))
    ? {
        ...newContract,
        startDate: newContract.startDate
          ? moment(newContract.startDate).format('YYYY-MM-DD')
          : undefined,
        endDate: newContract.endDate ? moment(newContract.endDate).format('YYYY-MM-DD') : null
      }
    : null
}

const getDiffAccount = (
  contract: ContractForm,
  formData: ContractForm
): Omit<PatchAccountModel, 'id'> | null => {
  const oldAccount = contractModelToPatchAccountModelMapper(contract)
  const newAccount = contractModelToPatchAccountModelMapper(formData)

  return !isEmpty(updatedDiff(oldAccount, newAccount)) ? newAccount : null
}

const getDiffPayment = (
  contract: ContractForm,
  formData: ContractForm
): Omit<PatchPaymentMethodModel, 'id'> | null => {
  const oldPayment = contractModelToPatchPaymentModelMapper(contract)
  const newPayment = contractModelToPatchPaymentModelMapper(formData)

  return !isEmpty(updatedDiff(oldPayment, newPayment)) ? newPayment : null
}

const getDiffAddress = (
  contract: ContractForm,
  formData: ContractForm
): Omit<PatchAddressModel, 'id'> | null => {
  const oldAddress = contractModelToPatchAddressModelMapper(contract)
  const newAddress = contractModelToPatchAddressModelMapper(formData)

  return !isEmpty(updatedDiff(oldAddress, newAddress)) ? newAddress : null
}

const getDiffCups = (
  cups: CupsFormModel,
  dbCups: CupsFormModel
): Omit<PatchCUPSModel, 'id'> | null => {
  const oldCUPS = cupsModelToPatchCupsModelMapper(dbCups)
  const newCUPS = cupsModelToPatchCupsModelMapper(cups)

  return !isEmpty(updatedDiff(oldCUPS, newCUPS)) ? newCUPS : null
}

const getDiffCupsAddress = (
  cups: CupsFormModel,
  dbCups: CupsFormModel
): PatchAddressModel | null => {
  let oldAddress: Omit<PatchAddressModel, 'id'> | null = null
  let newAddress: Omit<PatchAddressModel, 'id'> | null = null
  if (
    dbCups.countryId &&
    dbCups.provinceId &&
    dbCups.municipalityId &&
    dbCups.postalAddress &&
    dbCups.postalCode &&
    dbCups.addressId
  ) {
    oldAddress = cupsFormModelToPatchAddressModelMapper(dbCups)
    newAddress = cupsFormModelToPatchAddressModelMapper(cups)
  }

  return oldAddress && newAddress
    ? !isEmpty(updatedDiff(oldAddress, newAddress))
      ? { ...newAddress, id: dbCups.addressId! }
      : null
    : null
}

const getNewContractATR = (
  contractATR: ContractATRFormModel
): {
  newContractATR: PatchContractATR | null
  newContractATRDetail: PatchContractATRDetail | null
  newPowers: PatchPower[] | null
} => {
  let newContractATR: PatchContractATR | null = null
  let newContractATRDetail: PatchContractATRDetail | null = null
  let newPowers: PatchPower[] | null = null

  newContractATR = {
    type: contractATR.type,
    code: contractATR.code,
    cupsId: 1,
    companyId: getActiveCompany()?.id
  }
  newContractATRDetail = {
    startDate: moment(contractATR.startDate).format('YYYY-MM-DD'),
    endDate: moment(contractATR.endDate).format('YYYY-MM-DD'),
    tollType: contractATR.tollType
  }
  newPowers = getNewPowers(contractATR)

  return { newContractATR, newContractATRDetail, newPowers }
}

const getDiffContractATR = (
  contractATR: ContractATRFormModel,
  dbContractATR: ContractATRFormModel
): PatchContractATR | null => {
  const oldContract = contractATRFormModelToPatchModelMapper(dbContractATR)
  const newContract = contractATRFormModelToPatchModelMapper(contractATR)

  return oldContract && newContract
    ? !isEmpty(updatedDiff(oldContract, newContract))
      ? {
          ...newContract,
          id: dbContractATR.id!
        }
      : null
    : null
}

export const getDiffContractDetailATR = (
  contractATR: ContractATRFormModel,
  dbContractATR: ContractATRFormModel
): PatchContractATRDetail | null => {
  const newDetail = contractATRDetailFormModelToPatchModelMapper(contractATR)
  const oldDetail = contractATRDetailFormModelToPatchModelMapper(dbContractATR)
  return oldDetail && newDetail
    ? !isEmpty(updatedDiff(oldDetail, newDetail))
      ? { ...newDetail, id: dbContractATR.idDetail, idContractATR: dbContractATR.id }
      : null
    : null
}

const getDiffCapacities = (
  capacities: CapacityFormModel[],
  dbCapacities: CapacityFormModel[]
): PatchCapacity[] | null => {
  let diffCapacities: PatchCapacity[] = []

  // Patch Capacities
  dbCapacities.forEach(dbCap => {
    const modCapacity = capacities.find(cap => cap.id === dbCap.id)

    if (modCapacity && !modCapacity.delete) {
      const oldContractMapped = capacitiesFormModelToPatchModelMapper(dbCap)
      const newContractMapped = capacitiesFormModelToPatchModelMapper(modCapacity)

      const diff = updatedDiff(oldContractMapped, newContractMapped)
      const patch = !isEmpty(diff) ? { ...diff, id: dbCap.id } : null
      patch && diffCapacities.push(patch)
    }
  })

  return diffCapacities
}

const getNewContractDetailATR = (contractATR: ContractATRFormModel): PatchContractATRDetail => {
  return {
    idContractATR: contractATR.id,
    startDate: moment(contractATR.newStartDate).format('YYYY-MM-DD'),
    endDate: moment(contractATR.newEndDate).format('YYYY-MM-DD'),
    tollType: contractATR.tollType
  }
}

const getNewPowers = (contractATR: ContractATRFormModel): PatchPower[] => {
  const powers = contractATRFormToPowerModelMapper(contractATR)

  return powers.map(power => ({ value: power.value, period: power.period }))
}

const getDiffPowers = (
  contractATR: ContractATRFormModel,
  dbContractATR: ContractATRFormModel
): PatchPower[] | null => {
  const originalPowers = dbContractATR.periods || []
  const modifiedPowers = contractATRFormToPowerModelMapper(contractATR)

  const originalPowersIds = originalPowers.map(power => power.idPower)

  const modified: Period[] = []
  const newPowers: Period[] = []
  const deleted: Period[] = []

  // Check for modified and new powers
  modifiedPowers.forEach(power => {
    const originalIndex = originalPowersIds.indexOf(power.idPower)
    if (originalIndex >= 0) {
      const originalPower = originalPowers[originalIndex]
      if (originalPower.idPower && power.value && originalPower.value !== power.value) {
        modified.push(power)
      }
    } else if (power.idPower === 0 || power.idPower == null) {
      newPowers.push(power)
    }
  })

  // Check for deleted powers
  modifiedPowers.forEach(power => {
    if (power.value === 0 && power.idPower !== 0) {
      deleted.push(power)
    }
  })

  let powerPatches: PatchPower[] = [...newPowers, ...modified]

  deleted.forEach((power, index) => {
    const deleteItem = { idPower: power.idPower, delete: true }
    powerPatches.push(deleteItem)
  })

  return powerPatches.map(p => ({ ...p, idContractATRDetail: dbContractATR.idDetail }))
}

const contractATRFormToPowerModelMapper = (contractATR: ContractATRFormModel): Period[] => {
  let periods: Period[] = []
  const periodList = [1, 2, 3, 4, 5, 6]
  periodList.forEach(periodNumber => {
    if (contractATR[`period${periodNumber}`] || contractATR[`period${periodNumber}id`]) {
      periods.push({
        idPower: contractATR[`period${periodNumber}id`],
        value: contractATR[`period${periodNumber}`] ?? 0,
        period: periodNumber
      })
    }
  })

  return periods
}

// export const mapPowersToVersionTable = (
//   contractATR: ContractDetailATRModel
// ): ContractDetailATRModel => {
//   contractATR.powers?.forEach(power => {
//     ;(contractATR[`period${power.period}id`] = power.idPower),
//       (contractATR[`period${power.period}`] = power.value)
//   })

//   return contractATR
// }

export const contractATRFormToPatchPowerModelMapper = (
  contractATR: ContractATRFormModel,
  originalRowData: ATRVersionTableModel
): PatchPower[] => {
  let powers: PatchPower[] = []
  const periodList = [1, 2, 3, 4, 5, 6]
  periodList.forEach(periodNumber => {
    // Consider the period if it exists now OR existed before this edit (originalRowData),
    // otherwise a period created and then removed again within the same unsaved session
    // (no real idPower yet) is silently skipped and its stale "create" patch never gets cleared
    if (
      contractATR[`period${periodNumber}`] ||
      contractATR[`period${periodNumber}id`] ||
      originalRowData[`period${periodNumber}`] ||
      originalRowData[`period${periodNumber}id`]
    ) {
      // property exists
      if (contractATR[`period${periodNumber}`] !== originalRowData[`period${periodNumber}`]) {
        // property has changed
        if (
          contractATR[`period${periodNumber}`] === 0 ||
          contractATR[`period${periodNumber}`] == null
        ) {
          // property was deleted
          powers.push({
            idPower:
              contractATR[`period${periodNumber}id`] ?? originalRowData[`period${periodNumber}id`],
            delete: true,
            period: periodNumber,
            idContractATRDetail: contractATR.idDetail
          })
        } else {
          // property was updated or created if previously deleted
          powers.push({
            ...(contractATR[`period${periodNumber}id`] && {
              idPower: contractATR[`period${periodNumber}id`]
            }),
            value: contractATR[`period${periodNumber}`] ?? 0,
            period: periodNumber,
            ...(contractATR.idDetail && { idContractATRDetail: contractATR.idDetail })
          })
        }
      }
    }
  })
  return powers
}

export const contractATRFormToPostContractATRDetail = (
  contractATR: ContractATRFormModel
): PatchContractATRDetail => {
  return {
    idContractATR: contractATR.id,
    startDate: moment(contractATR.newStartDate).format('YYYY-MM-DD'),
    endDate: moment(contractATR.newEndDate).format('YYYY-MM-DD'),
    tollType: contractATR.tollType
  }
}

export const contractATRFormToPostPower = (contractATR: ContractATRFormModel): Period[] => {
  let periods: Period[] = []
  const periodList = [1, 2, 3, 4, 5, 6]
  periodList.forEach(periodNumber => {
    if (contractATR[`period${periodNumber}`] || contractATR[`period${periodNumber}id`]) {
      periods.push({
        value: contractATR[`period${periodNumber}`] ?? 0,
        period: periodNumber
      })
    }
  })

  return periods
}

const getDiffProductsList = (
  formData: NewContractProduct[],
  { contractId, cupsId }: { contractId: number; cupsId?: number }
): PatchContractProductModel[] | undefined => {
  let request: PatchContractProductModel[] = []
  formData.forEach(product => {
    if (product.tempId) {
      const newProd: PatchContractProductModel = {
        productId: product.productId,
        contractId: contractId,
        contractProductId: product.id,
        startDate: product.startDate ? moment(product.startDate).format('YYYY-MM-DD') : undefined,
        endDate: product.endDate ? moment(product.endDate).format('YYYY-MM-DD') : undefined,
        cupsId: cupsId
      }
      request.push(newProd)
    }
  })

  return request.length > 0 ? request : undefined
}

const getDeletedProducts = (
  products: NewContractProduct[],
  databaseProducts: ProductModel[] | undefined
): DeleteContractProductModel[] => {
  let deletedProducts: DeleteContractProductModel[] = []
  databaseProducts?.map(oldProduct => {
    const foundProduct = products.find(product => product.id === oldProduct.contractProductId)
    if (!foundProduct) {
      deletedProducts.push({ contractProductId: oldProduct.contractProductId! })
    }
  })
  return deletedProducts
}

export const contractModelToPatchAddressModelMapper = (
  contract: ContractForm
): Omit<PatchAddressModel, 'id'> => {
  return {
    postalAddress: contract.postalAddress,
    postalCode: contract.postalCode,
    countryId: contract.countryId,
    provinceId: contract.provinceId,
    municipalityId: contract.municipalityId
  }
}

export const cupsModelToPatchCupsModelMapper = (
  cups: CupsFormModel
): Omit<PatchCUPSModel, 'id'> => {
  return {
    code: cups.code,
    cnae: cups.cnaeCode,
    pressureGroupId: cups.pressureGroup,
    telemetered: cups.telemetered,
    height: cups.height
  }
}

export const cupsFormModelToPatchAddressModelMapper = (
  cups: CupsFormModel
): Omit<PatchAddressModel, 'id'> => {
  return {
    countryId: cups.countryId,
    provinceId: cups.provinceId,
    municipalityId: cups.municipalityId,
    postalCode: cups.postalCode,
    postalAddress: cups.postalAddress
  }
}

export const contractATRFormModelToPatchModelMapper = (
  contractATR: ContractATRFormModel
): Omit<PatchContractATR, 'id'> => {
  return {
    type: contractATR.type,
    code: contractATR.code,
    state: contractATR.state
  }
}

export const contractATRDetailFormModelToPatchModelMapper = (
  contractATR: ContractATRFormModel
): Omit<PatchContractATRDetail, 'id'> => {
  return {
    tollType: contractATR.tollType,
    startDate: contractATR.startDate
      ? moment(contractATR.startDate).format('YYYY-MM-DD')
      : undefined,
    endDate: contractATR.endDate ? moment(contractATR.endDate).format('YYYY-MM-DD') : undefined
  }
}

export const consolidateATRState = (contractATR: ContractATRFormModel): ContractATRFormModel => {
  return { ...contractATR, state: contractATR.state ?? EnumEstadoProductoCapacidad.Activo }
}

export const mapATRVersionTableToContractDetails = (
  rowContractDetails: ATRVersionTableModel
): ContractATRFormModel => {
  return {
    version: rowContractDetails.version[0],
    idDetail: rowContractDetails.idDetail,
    tollType: rowContractDetails.tollType!,
    startDate: moment(rowContractDetails.startDate, 'YYYY-MM-DD').toDate(),
    endDate: moment(rowContractDetails.endDate, 'YYYY-MM-DD').toDate(),
    period1: rowContractDetails.period1,
    period2: rowContractDetails.period2,
    period3: rowContractDetails.period3,
    period4: rowContractDetails.period4,
    period5: rowContractDetails.period5,
    period6: rowContractDetails.period6,
    period1id: rowContractDetails.period1id,
    period2id: rowContractDetails.period2id,
    period3id: rowContractDetails.period3id,
    period4id: rowContractDetails.period4id,
    period5id: rowContractDetails.period5id,
    period6id: rowContractDetails.period6id,
    periods: rowContractDetails.powers ? rowContractDetails.powers : [],
    canEdit: rowContractDetails.canEdit
  }
}

// FormModel -> PatchFormModel (Post Capacity)
export const newCapacitiesFormModelToPatchModelMapper = (
  capacity: CapacityFormModel
): Omit<PatchCapacity, 'id'> => {
  return {
    code: capacity.code,
    contractType: capacity.contractType,
    toll: capacity.toll,
    type: capacity.type,
    // rawMaterial: capacity.rawMaterial, // US52694 -> remove rawMaterial
    qaEstimada: capacity.qaEstimada,
    qdContratada: capacity.qdContratada,
    startDate: capacity.startDate ? moment(capacity.startDate).format('YYYY-MM-DD') : undefined,
    endDate: capacity.endDate ? moment(capacity.endDate).format('YYYY-MM-DD') : undefined,
    signDate: capacity.signDate ? moment(capacity.signDate).format('YYYY-MM-DD') : undefined,
    cupsId: capacity.cupsId,
    logisticElementId: capacity.logisticElementId,
    companyId: getActiveCompany()?.id,
    balanceZone: getActiveBalanceZone()?.id,
    status: capacity.status
  }
}

// FormModel -> PatchFormModel (Patch Capacity)
export const capacitiesFormModelToPatchModelMapper = (
  capacity: CapacityFormModel
): Omit<PatchCapacity, 'id'> => {
  return {
    code: capacity.code,
    contractType: capacity.contractType,
    toll: capacity.toll,
    type: capacity.type,
    rawMaterial: capacity.rawMaterial,
    qaEstimada: capacity.qaEstimada,
    qdContratada: capacity.qdContratada,
    startDate: capacity.startDate ? moment(capacity.startDate).format('YYYY-MM-DD') : undefined,
    endDate: capacity.endDate ? moment(capacity.endDate).format('YYYY-MM-DD') : undefined,
    nmHours: capacity.nmHours,
    status: capacity.status
  }
}

export const contractCapacitiesMapper = (
  capacity: CapacityFormModel,
  contractDetails: ContractModel | undefined,
  cups: Partial<CupsFormModel>
) => {
  return {
    id: capacity.id,
    ...newCapacitiesFormModelToPatchModelMapper(capacity),
    cupsId: cups.id,
    logisticElementId: cups.logisticElementId,
    signDate: contractDetails?.signatureDate
  }
}

export const cupsCNAEMapper = (
  cups: Partial<CupsFormModel>,
  CNAEList: ActivityTypeModel[]
): Partial<CupsFormModel> => {
  const cnaeId = CNAEList.find(cnae => cnae.code === cups.cnaeCode)?.id
  // Preserve existing cnaeId if no match is found in the list
  return { ...cups, cnaeId: cnaeId ?? cups.cnaeId }
}

export const contractModelToPatchPaymentModelMapper = (
  contract: ContractForm
): Omit<PatchPaymentMethodModel, 'id'> => {
  return {
    type: `${contract.paymentMethodType}`,
    iban: contract.iban
  }
}

export const contractModelToPatchContractModelMapper = (
  contract: ContractForm
): Omit<PatchContractModel, 'id'> => {
  return {
    contractCode: contract.contractCode,
    startDate: moment(contract.startDate).format('YYYY-MM-DD'),
    endDate: contract.endDate ? moment(contract.endDate).format('YYYY-MM-DD') : undefined,
    signatureDate: contract.signatureDate
      ? moment(contract.signatureDate).format('YYYY-MM-DD')
      : undefined,
    status: contract.state,
    description: contract.description,
    bookID: contract.bookID,
    hold: contract.hold,
    type: contract.type
  }
}

export const contractModelToPatchAccountModelMapper = (
  contract: ContractForm
): Omit<PatchAccountModel, 'id'> => {
  return {
    code: contract.accountCode,
    billingCycleId: contract.billingCycleId
  }
}

export const contractModelToContractFormMapper = (
  contract?: ContractModel | null
): Partial<ContractFormRaw> | null => {
  return contract
    ? {
        id: contract.id,
        contractCode: contract.contractCode,
        startDate: moment(contract.startDate).toDate(),
        endDate: contract.endDate ? moment(contract.endDate).toDate() : null,
        signatureDate: moment(contract.signatureDate).toDate(),
        expectedStartDate: contract.expectedStartDate
          ? moment(contract.expectedStartDate).toDate()
          : null,
        expectedEndDate: contract.expectedEndDate
          ? moment(contract.expectedEndDate).toDate()
          : null,
        state: contract.status
          ? enumEnumToValueNumber(EnumEstadoCliente, contract.status)
          : undefined,
        type: enumEnumToValueNumber(EnumTipoContrato, contract.type),
        accountId: contract.account?.id,
        accountCode: contract.account?.code ?? '',
        iban: contract.paymentMethod?.iban,
        billingCycleId: contract.account?.billingCycleId,
        paymentMethodId: contract.paymentMethod?.Id,
        paymentMethodType:
          enumEnumToValueNumber(EnumTipoMetodoPago, contract.paymentMethod?.type) ?? 0,
        fiscalAddressId: contract.paymentMethod?.address?.Id,
        postalAddress: contract.paymentMethod?.address?.postalAddress,
        postalCode: contract.paymentMethod?.address?.postalCode,
        postalCodeSearch: {
          value: contract.paymentMethod?.address?.postalCode,
          label: contract.paymentMethod?.address?.postalCode,
          textValue: contract.paymentMethod?.address?.postalCode
        },
        municipalityId: contract.paymentMethod?.address?.municipalityId,
        municipality: contract.paymentMethod?.address?.municipality,
        provinceId: contract.paymentMethod?.address?.provinceId,
        province: contract.paymentMethod?.address?.province,
        countryId: contract.paymentMethod?.address?.countryId,
        country: contract.paymentMethod?.address?.country,
        cupsId:
          contract.cupsList && contract.cupsList.length > 0 ? contract.cupsList[0].id : undefined,
        products: [],

        description: contract.description,
        bookID: contract.bookID,
        hold: enumEnumToValueNumber(EnumHold, contract.hold),
        commodity: enumEnumToValueNumber(EnumCommodity, contract.commodity) ?? 1,
        productsList: contract.productsList,
        annexedContracts: contract.annexedContracts ?? []
      }
    : null
}

export const getCurrentVersion = (contractATR: ContractATRModel) => {
  const today: Moment = moment().startOf('day')

  return contractATR.versions.find(version => {
    const startDate: Moment = moment(version.startDate).startOf('day')
    const endDate: Moment | null = version.endDate ? moment(version.endDate).startOf('day') : null

    return startDate <= today && endDate && today <= endDate
  })
}

export const getLatestVersion = (contractATR: ContractATRModel) => {
  return contractATR.versions.reduce((lastVersion, version) => {
    return (version.version ?? 0 > lastVersion.version ?? 0) ? version : lastVersion
  })
}

export const contractModelToATRMapper = (contractATR: ContractATRModel): ContractATRFormModel => {
  const currentVersion = getCurrentVersion(contractATR) ?? getLatestVersion(contractATR)

  return {
    ...contractATR,
    tollType: enumEnumToValueNumber(EnumTipoPeaje, currentVersion.tollType) ?? null,
    startDate: currentVersion.startDate,
    endDate: currentVersion.endDate,
    idDetail: currentVersion.idDetail,
    version: currentVersion.version ?? 0,
    periods: currentVersion.powers?.map(power => ({ ...power, period: power.period })),
    period1id: currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo1)?.idPower,
    period2id: currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo2)?.idPower,
    period3id: currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo3)?.idPower,
    period4id: currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo4)?.idPower,
    period5id: currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo5)?.idPower,
    period6id: currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo6)?.idPower,
    period1: currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo1)?.value,
    period2: currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo2)?.value,
    period3: currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo3)?.value,
    period4: currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo4)?.value,
    period5: currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo5)?.value,
    period6: currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo6)?.value
  }
}

export const mapATRVersionsToTableModel = (
  versions: ContractDetailATRModel[] | null,
  state: EnumEstadoProductoCapacidad | undefined
): ATRVersionTableModel[] => {
  if (!versions) {
    return []
  }

  let max: number = 0
  let index: number = 0

  // iterate array to map versionATR and find maximum version number within the same operation
  let mappedVersions = versions.map((version, arrayIndex) => {
    if (version.version > max) {
      max = version.version
      index = arrayIndex
    }
    return mapATRVersionToTableModel(version, state)
  })

  // /* latest versionATR must be editable to handle the possibility to extend version endDate,
  // whether it is currentOrFuture or not */
  // mappedVersions[index].canEdit = true

  mappedVersions[index].canCreateProcedure =
    mappedVersions[index].startDate &&
    moment(mappedVersions[index].startDate).isSameOrAfter(moment(), 'day') &&
    state === EnumEstadoProductoCapacidad.Borrador

  return mappedVersions
}

export const mapATRVersionToTableModel = (
  version: ContractDetailATRModel,
  state: EnumEstadoProductoCapacidad | undefined
): ATRVersionTableModel => {
  return {
    ...version,
    startDate: formatDateOnlyDate(version.startDate)!,
    endDate: formatDateOnlyDate(version.endDate),
    version: [version.version ?? 0],
    tollTypeText: version.tollType
      ? (enumTranslateOption(version.tollType, EnumTipoPeaje, 'enums:EnumTipoPeaje') ?? '')
      : '',
    tollType: version.tollType,
    period1: version.powers?.find(power => power.period === EnumPeriodo.Periodo1)?.value,
    period2: version.powers?.find(power => power.period === EnumPeriodo.Periodo2)?.value,
    period3: version.powers?.find(power => power.period === EnumPeriodo.Periodo3)?.value,
    period4: version.powers?.find(power => power.period === EnumPeriodo.Periodo4)?.value,
    period5: version.powers?.find(power => power.period === EnumPeriodo.Periodo5)?.value,
    period6: version.powers?.find(power => power.period === EnumPeriodo.Periodo6)?.value,
    period1id: version.powers?.find(power => power.period === EnumPeriodo.Periodo1)?.idPower,
    period2id: version.powers?.find(power => power.period === EnumPeriodo.Periodo2)?.idPower,
    period3id: version.powers?.find(power => power.period === EnumPeriodo.Periodo3)?.idPower,
    period4id: version.powers?.find(power => power.period === EnumPeriodo.Periodo4)?.idPower,
    period5id: version.powers?.find(power => power.period === EnumPeriodo.Periodo5)?.idPower,
    period6id: version.powers?.find(power => power.period === EnumPeriodo.Periodo6)?.idPower,
    canEdit: canEditATR(state)
  }
}

export const canEditATR = (state: EnumEstadoProductoCapacidad | undefined): boolean => {
  const stateValue = enumEnumToValueNumber(EnumEstadoProductoCapacidad, state)

  return (
    stateValue === EnumEstadoProductoCapacidad.Borrador ||
    stateValue === EnumEstadoProductoCapacidad.Rechazada
  )
}

export const versionIsCurrentOrFuture = (endDate: Date | undefined): boolean => {
  return !endDate || moment(endDate).startOf('day').isSameOrAfter(moment().startOf('day'))
}

export const mapAddVersionToTableModel = (
  formData: ContractATRAddVersionFormModel
): ATRVersionTableModel => {
  let versionNumber: any
  let startDate: Date
  let endDate: Date

  // is a new Version to ADD
  versionNumber = (parseInt(formData.latestVersionNumber[0]) || 0) + 1
  startDate = formData.newStartDate!
  endDate = formData.newEndDate!

  return {
    ...formData,
    actions: associateActionIcon(formData, ActionIcons.NEW_ITEM, 'No Guardado'),
    ...(formData.idDetail && { idDetail: formData.idDetail }),
    startDate: startDate,
    endDate: endDate,
    tollTypeText: formData.tollType
      ? (enumTranslateOption(formData.tollType, EnumTipoPeaje, 'enums:EnumTipoPeaje') ?? '')
      : '',
    tollType: formData.tollType,
    version: [versionNumber],
    period1: formData.period1,
    period2: formData.period2,
    period3: formData.period3,
    period4: formData.period4,
    period5: formData.period5,
    period6: formData.period6,
    canEdit: true // versionIsCurrentOrFuture(endDate)  -> replaced, user should be able to edit what has just add, just in case of mistake
  }
}

export const mapUpdateVersionToTableModel = (
  formData: ContractATRAddVersionFormModel | ContractATRFormModel
): ATRVersionTableModel => {
  let versionNumber: any
  let startDate: Date
  let endDate: Date

  // if ('latestVersionNumber' in formData) {
  //   // is a new Version to ADD
  //   versionNumber = formData.latestVersionNumber[0] + 1
  //   startDate = formData.newStartDate!
  //   endDate = formData.newEndDate!
  // } else {
  //   // is a new Version to EDIT
  versionNumber = formData.version[0]
  startDate = formData.startDate
  endDate = formData.endDate!
  // }

  return {
    ...formData,
    actions: associateActionIcon(formData, ActionIcons.NEW_ITEM, 'No Guardado'),
    idDetail: formData.idDetail,
    startDate: startDate,
    endDate: endDate,
    tollTypeText: formData.tollType
      ? (enumTranslateOption(formData.tollType, EnumTipoPeaje, 'enums:EnumTipoPeaje') ?? '')
      : '',
    tollType: formData.tollType,
    version: [versionNumber],
    period1: formData.period1,
    period2: formData.period2,
    period3: formData.period3,
    period4: formData.period4,
    period5: formData.period5,
    period6: formData.period6,
    canEdit: formData.canEdit
  }
}

export const capacityModelListToFormModelMapper = (
  capacities: CapacityModel[]
): CapacityFormModel[] => {
  const mappedCapacities = capacities.map(capacity => capacityModelToFormModelMapper(capacity))

  // Only the most recent capacity (startDate, then id) can request a modification
  const latestCapacity = mappedCapacities
    .filter(capacity => capacity.canRequestCapacityMod)
    .sort(
      (a, b) =>
        moment(b.startDate).valueOf() - moment(a.startDate).valueOf() || (b.id ?? 0) - (a.id ?? 0)
    )[0]

  return mappedCapacities.map(capacity => ({
    ...capacity,
    canRequestCapacityMod: !!latestCapacity && capacity.id === latestCapacity.id
  }))
}

export const capacityModelToFormModelMapper = (capacity: CapacityModel): CapacityFormModel => {
  return {
    ...capacity,
    // rawMaterial: capacity.rawMaterial ?? false, // US52694 -> remove rawMaterial
    status: enumEnumToValueNumber(EnumEstadoProductoCapacidad, capacity.status) ?? 0,
    type: enumEnumToValueNumber(EnumTipoProductoCapacidad, capacity.type) ?? 0,
    contractType: enumEnumToValueNumber(EnumServicio, capacity.contractType) ?? 0,
    toll: enumEnumToValueNumber(EnumTipoPeaje, capacity.toll) ?? 0,
    canEdit: true,
    canRequestCapacityMod:
      (enumEnumToValueNumber(EnumEstadoProductoCapacidad, capacity.status) ===
        EnumEstadoProductoCapacidad.Formalizada ||
        enumEnumToValueNumber(EnumEstadoProductoCapacidad, capacity.status) ===
          EnumEstadoProductoCapacidad.Activo) &&
      enumEnumToValueNumber(EnumTipoProductoCapacidad, capacity.type) ===
        EnumTipoProductoCapacidad.Indefinido
        ? true
        : false,
    canTerminateCapacity:
      (enumEnumToValueNumber(EnumEstadoProductoCapacidad, capacity.status) ===
        EnumEstadoProductoCapacidad.Formalizada ||
        enumEnumToValueNumber(EnumEstadoProductoCapacidad, capacity.status) ===
          EnumEstadoProductoCapacidad.Activo) &&
      enumEnumToValueNumber(EnumTipoProductoCapacidad, capacity.type) !==
        EnumTipoProductoCapacidad.Indefinido
  }
}

export const newCapacityFormModelConsolidate = (capacity: CapacityFormModel): CapacityFormModel => {
  return {
    ...capacity,
    status: EnumEstadoProductoCapacidad.Borrador
    // rawMaterial: capacity.rawMaterial ?? false // deleted from US 52694
  }
}

export const cupsModelToFormModelMapper = (
  cups: CUPSModel,
  cnaeList?: ActivityTypeModel[]
): CupsFormModel => {
  // Map cnaeId if cnaeList is provided and cnae code exists
  const cnaeId = cnaeList?.find(cnae => cnae.code === cups.cnae)?.id

  return {
    id: cups.id,
    code: cups.code,
    distributorId: cups.distributor?.id,
    pressureGroup: cups.pressureGroupId,
    telemetered: cups.telemetered ?? false,
    cnaeCode: cups.cnae,
    cnaeId: cnaeId,
    countryId: cups.fiscalAddress?.countryId,
    country: cups.fiscalAddress?.country,
    provinceId: cups.fiscalAddress?.provinceId,
    province: cups.fiscalAddress?.province,
    municipalityId: cups.fiscalAddress?.municipalityId,
    municipality: cups.fiscalAddress?.municipality,
    postalAddress: cups.fiscalAddress?.postalAddress,
    postalCode: cups.fiscalAddress?.postalCode,
    addressId: cups.fiscalAddress?.Id,
    commodity: cups.commodity,
    logisticElementId: cups.logisticElement?.id,
    height: cups.height
  }
}

export const contractProductAPIModelListMapper = (
  products: ContractProductAPIModel[]
): ContractProduct[] => {
  return products ? products.map(contractProductAPIModelToContractProductMapper) : []
}

export const contractProductAPIModelToContractProductMapper = (
  product: ContractProductAPIModel
): ContractProduct => {
  return {
    id: product.id,
    contractId: product.contractId,
    productId: product.productId,
    code: product.productCode,
    description: product.productDescription,
    startDate: moment(product.startDate).toDate(),
    endDate: product.endDate ? moment(product.endDate).toDate() : null
  }
}

export interface ProductSelectOption extends SelectOption {
  code: string
  startDate?: Date
  endDate?: Date
}

export const contractProductAPIModelListToOptionsMapper = (
  products: ContractProductAPIModel[]
): ProductSelectOption[] => {
  return products
    ? products.map(product => ({
        value: product.productId,
        label: product.productDescription,
        code: product.productCode,
        startDate: product.startDate ? moment(product.startDate).toDate() : undefined,
        endDate: product.endDate ? moment(product.endDate).toDate() : undefined
      }))
    : []
}

export const changeOwnerNewContractMapper = (
  dataForm: ChangeOwnerForm,
  activeContract: ActiveContractModelResponse
): Omit<NewContractModel, 'contractCode'> => {
  return {
    // contractCode: activeContract.contractCode
    status: EnumEstadoCliente.Activo,
    type: activeContract.type,
    startDate: moment(dataForm.newStartDate).format('YYYY-MM-DD'),
    endDate: moment(dataForm.newEndDate).format('YYYY-MM-DD'),
    signatureDate: moment(dataForm.signDate).format('YYYY-MM-DD'),
    balanceZone: activeContract.balanceZone,
    paymentMethod: {
      type: dataForm.paymentMethodId,
      iban: dataForm.iban
    },
    billingCycleID: activeContract.billingCycleID,
    cupsList: [],
    productsList: dataForm.activeProductCode
      ? [
          {
            code: dataForm.activeProductCode
          }
        ]
      : [],
    quotesList: dataForm.activeQuoteId
      ? [
          {
            id: dataForm.activeQuoteId
          }
        ]
      : [],
    companyID: activeContract.companyID,
    commodity: activeContract.commodity
  }
}

// expor

export const parseDate = (date?: Date): string => {
  return moment(date).format('YYYY-MM-DD')
}

const contractModelToDataFormModel = (contract: ContractModel): ContractDataFormModel => {
  return (
    contract && {
      contractCode: contract.contractCode,
      billingCycle: contract.billingCycleID,
      paymentMethod: contract.paymentMethod.type,
      iban: contract.paymentMethod.iban
    }
  )
}

export const getATRStartDateHelpText = ({
  contractATR,
  originalContractATR
}: {
  contractATR: Partial<ContractATRFormModel> | null
  originalContractATR: ContractATRFormModel | null
}) => {
  if (!contractATR || !originalContractATR) {
    return undefined
  }

  const editingATR = contractATR.id
  const multipleATRVersions = contractATR.version && contractATR.version > 0
  const willGenerateNewVersion = contractATR.generateNewVersion
  const startDateChanged = !moment(originalContractATR?.startDate).isSame(
    contractATR?.startDate,
    'days'
  )
  if (
    editingATR &&
    multipleATRVersions &&
    !willGenerateNewVersion &&
    startDateChanged &&
    contractATR.version
  ) {
    return `La fecha de fin de la versión ${
      contractATR.version - 1
    } será modificada al ${toLocaleDate(moment(contractATR.startDate).add(-1, 'day').toDate())}`
  }

  return undefined
}

export const limitToNewContractQuantityMapper = (limit: Limit): NewContractQuantity => {
  return {
    measureUnit: limit.measureUnit,
    details: limit.details ? newlimitDetailsToContractQuantityDetailMapper(limit.details) : []
  }
}

export const limitToContractQuantityRequestMapper = (limit: Limit): ContractQuantityRequest => {
  return {
    id: limit.id!,
    measureUnit: limit.measureUnit,
    quantityType: limit.quantityType,
    details: limit.details ? limitDetailsToContractQuantityDetailRequestMapper(limit.details) : []
  }
}

export const limitDetailsToContractQuantityDetailRequestMapper = (
  details: LimitDetail[]
): ContractQuantityDetailRequest[] => {
  return details.map(limitDetailToContractQuantityDetailMapper)
}

export const limitDetailToContractQuantityDetailMapper = (
  detail: LimitDetail
): ContractQuantityDetailRequest => {
  return {
    idDetail: detail.idDetail,
    quantity: detail.quantity,
    startDate: moment(detail.startDate).format('YYYY-MM-DD'),
    endDate: moment(detail.endDate).format('YYYY-MM-DD')
  }
}

export const newlimitDetailsToContractQuantityDetailMapper = (
  details: LimitDetail[]
): NewContractQuantityDetail[] => {
  return details.map(newlimitDetailToContractQuantityDetailMapper)
}

export const newlimitDetailToContractQuantityDetailMapper = (
  detail: LimitDetail
): NewContractQuantityDetail => {
  return {
    quantity: detail.quantity,
    startDate: moment(detail.startDate).format('YYYY-MM-DD'),
    endDate: moment(detail.endDate).format('YYYY-MM-DD')
  }
}

export const accountsToTableFormatMapper = (accounts: Account[]): AccountTableFormat[] => {
  return accounts.map(account => ({
    id: account.id,
    code: account.code,
    iban: account.paymentMethod.iban,
    billingCycleId: account.billingCycleId,
    paymentMethodType: enumEnumToValueNumber(EnumTipoMetodoPago, account.paymentMethod.type)!,
    paymentMethod: account.paymentMethod,
    postalAddress: account.paymentMethod.address.postalAddress,
    postalCode: account.paymentMethod.address.postalCode,
    municipality: account.paymentMethod.address.municipality,
    province: account.paymentMethod.address.province,
    country: account.paymentMethod.address.country,
    canSelect: true,
    canRemove: false
  }))
}

export const mapResponseToContractModelArray = (contracts: ContractModel[]): ContractModel[] => {
  return contracts.map(contract => mapResponseToContractModel(contract))
}

export const mapResponseToContractModel = (contract: ContractModel): ContractModel => {
  return {
    ...contract,
    commodity: enumValueToKey(EnumCommodity, contract.commodity)!
  }
}

export const mapAnnexedParamsToSelectOption = (
  annexedParams: AnnexedParamsModel[]
): SelectOption[] => {
  return annexedParams.map(param => mapAnnexedParamToSelectOption(param))
}

export const mapAnnexedParamToSelectOption = (param: AnnexedParamsModel): SelectOption => ({
  label: param.detailedDescription ?? '',
  value: param.id ?? 0
})

export const mapAnnexedParamValuesToSelectOption = (
  annexedParams: AnnexedParamsValuesModel[]
): SelectOption[] => {
  return annexedParams.map(param => mapAnnexedParamValueToSelectOption(param))
}

export const mapAnnexedParamValueToSelectOption = (
  param: AnnexedParamsValuesModel
): SelectOption => ({
  label: `${param.code} - ${param.description ?? ''}`,
  value: param.id ?? 0
})

const mapValueRequestByType = (
  valueTypeId: EnumTipoValor,
  formData: AnnexedContractForm
): string => {
  switch (valueTypeId) {
    case EnumTipoValor.Int:
      return formData.value_number?.toFixed(0) || ''
    case EnumTipoValor.Decimal:
      return formData.value_number?.toFixed(2) || ''
    case EnumTipoValor.Char:
    case EnumTipoValor.String:
      return formData.value_text || ''
    case EnumTipoValor.Bool:
      return formData.value_bool ? 'S' : 'N'
    case EnumTipoValor.DateTime:
      return moment(formData.value_date!).format('YYYY-MM-DD')
    case EnumTipoValor.List:
      return formData.value_list_label?.split(' - ')[0] || ''
    default:
      return formData.value_text || ''
  }
}

export const mapAnnexedContractToPostRequest = (
  formData: AnnexedContractForm
): AnnexedContractModelPostRequest => {
  const request: AnnexedContractModelPostRequest = {
    annexedParam: formData.annexedParamId!,
    value: mapValueRequestByType(formData.valueTypeId!, formData)
  }

  return request
}

export const mapAnnexedContractToPatchRequest = (
  formData: AnnexedContractForm,
  contractId: number
): AnnexedContractModelPatchRequest => {
  const request: AnnexedContractModelPatchRequest = {
    annexedContractId: formData.annexedContractId!,
    contractId: contractId,
    annexedParam: formData.annexedParamId!,
    value: mapValueRequestByType(formData.valueTypeId!, formData)
  }

  return request
}

export const mapSelectedRowToAnnexedContractForm = (
  selectedRow: AnnexedContractModel,
  paramValues?: AnnexedParamsValuesModel[]
): AnnexedContractForm => {
  let formData: AnnexedContractForm = {
    annexedParamId: selectedRow.annexedParam,
    annexedContractId: selectedRow.annexedContractId,
    valueTypeId: selectedRow.valueType
  }

  switch (selectedRow.valueType) {
    case EnumTipoValor.Int:
    case EnumTipoValor.Decimal:
      formData = { ...formData, value_number: +selectedRow.value }
      break
    case EnumTipoValor.Char:
    case EnumTipoValor.String:
      formData = { ...formData, value_text: selectedRow.value }
      break
    case EnumTipoValor.Bool:
      formData = { ...formData, value_bool: selectedRow.value === 'S' ? true : false }
      break
    case EnumTipoValor.DateTime:
      formData = { ...formData, value_date: moment(selectedRow.value).toDate() }
      break
    case EnumTipoValor.List:
      // Reconstruir el label con formato "código - descripción" si se proporciona la lista de valores
      const paramValue = paramValues?.find(pv => pv.code === selectedRow.value)
      const listLabel = paramValue
        ? `${paramValue.code} - ${paramValue.description ?? ''}`
        : selectedRow.value
      formData = { ...formData, value_list_label: listLabel }
      break
    default:
      formData = { ...formData, value_text: selectedRow.value }
      break
  }

  return formData
}

export const mapContractOrdersModelToRequest = (
  formData: ContractOrdersModel
): ContractOrdersRequestModel => {
  return {
    ...formData,
    startDate: moment(formData.startDate).format('YYYY-MM-DD'),
    endDate: moment(formData.endDate).format('YYYY-MM-DD')
  }
}

export const contractProductToSVAProductMapper = (
  rowData: NewContractProduct
): Partial<NewContractProduct> | null => {
  return rowData
    ? {
        contractId: rowData.contractId,
        currency: rowData.currency,
        endDate: rowData.endDate,
        startDate: rowData.startDate,
        id: rowData.id,
        productId: rowData.productId,
        productCode: rowData.productCode!,
        productDescription: rowData.productDescription!
      }
    : null
}

export const contractDataToSVAContractMapper = (
  contractData: Partial<ContractFormRaw> | null
): (ContractSummaryModel & { commodity: EnumCommodity }) | null => {
  return contractData
    ? {
        id: contractData?.id!,
        code: contractData?.contractCode!,
        description: contractData?.description!,
        commodity: contractData?.commodity!
      }
    : null
}

export const clientDataToSVAClientMapper = (
  clientData: SubjectModel | undefined
): Partial<SubjectModel> | null => {
  return clientData
    ? {
        id: clientData?.id!,
        name: clientData?.name!,
        documentID: clientData?.documentID!,
        businessName: clientData?.businessName!,
        surname1: clientData?.surname1!,
        surname2: clientData?.surname2!
      }
    : null
}
